import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Layers,
  Search,
  Plus,
  Edit,
  Trash2,
  RotateCcw,
  History,
  XCircle,
  BarChart2,
  User,
  Clock,
} from "lucide-react";
import { format } from "date-fns";
import Loader from "../../../loader/Loader";
import AddTerms from "../TermsModule/AddTerms";
import AuditInfoModal, { buildAuditRows } from "../../../shared/AuditInfoModal"; // ✅ Added
import Pagination from "../../Pagination";
import { domain } from "../../../../security";
import { selectUserID } from "../../../../redux/IchthusSlice";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return format(new Date(dateString), "MMM dd, yyyy");
};

// ==========================================
// HISTORY MODAL COMPONENT
// ==========================================
const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null); // ✅ Audit inside history modal

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${domain}/api/Terms/history?search=${search}`,
      );
      setHistoryData(response.data);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = historyData.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search history..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <History size={20} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                No deleted records found.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Desktop Table */}
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-1">
                          {item.description}
                          {/* ✅ Audit button in history table */}
                          <button
                            onClick={() => setAuditItem(item)}
                            className="text-amber-500 hover:text-amber-700 ml-1"
                            title="View Audit Info"
                          >
                            <Clock size={14} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className="font-medium text-red-500">
                            {item.deletedByFullName || "Unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-red-500 text-xs">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center justify-end gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile History Stack List */}
              <div className="sm:hidden flex flex-col gap-3">
                {currentItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-slate-800">
                        {item.description}
                      </h4>
                      <div className="flex items-center gap-2">
                        {/* ✅ Audit button mobile history */}
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-2 bg-amber-50 text-amber-500 rounded-lg hover:bg-amber-100 transition"
                          title="View Audit Info"
                        >
                          <Clock size={16} />
                        </button>
                        <button
                          onClick={async () => {
                            await onRestore(item.id);
                            fetchHistory();
                          }}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                        >
                          <RotateCcw size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <User size={10} className="text-slate-400" />
                        Created:{" "}
                        <span className="font-medium text-slate-700">
                          {item.createdByFullName || "System"}
                        </span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Trash2 size={10} className="text-red-400" />
                        Deleted:{" "}
                        <span className="font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={historyData.length}
                  currentPage={currentPage}
                  paginate={paginate}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ AuditInfoModal inside history modal */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.description}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const AllTerms = () => {
  const currentUserID = useSelector(selectUserID);

  const [termsData, setTermsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedData, setSelectedData] = useState(null);

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [auditItem, setAuditItem] = useState(null); // ✅ Audit state

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTerms, setFilteredTerms] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${domain}/api/Terms`);
      setTermsData(response.data);
    } catch {
      toast.error("Failed to fetch Terms.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const results = termsData.filter((item) =>
      (item.description || "").toLowerCase().includes(lowerSearch),
    );
    setFilteredTerms(results);
    setCurrentPage(1);
  }, [searchTerm, termsData]);

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Term?"))
      return;
    try {
      await axios.put(
        `${domain}/api/Terms/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Term Moved to History!");
      fetchData();
    } catch {
      toast.error("Failed to delete Term.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/Terms/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Term Restored Successfully!");
      fetchData();
    } catch {
      toast.error("Failed to restore Term.");
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTerms.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="min-h-screen pb-20">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* --- Header --- */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Layers className="text-indigo-600" /> Terms
              </h1>
              <p className="text-xs md:text-sm text-slate-500 mt-1 hidden sm:block">
                Agreed period and conditions for payment.
              </p>
            </div>

            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <History size={18} />
                <span className="md:inline">History</span>
              </button>
              <button
                onClick={() => {
                  setSelectedData(null);
                  setIsAddModalVisible(true);
                }}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 transition-all"
              >
                <Plus size={18} />
                <span className="md:inline">Add New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-2">
        {/* --- Stats & Search --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Active Terms
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {filteredTerms.length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search term..."
                className="w-full pl-10 pr-4 py-2 text-sm md:text-base border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            {/* 1. DESKTOP VIEW */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Description</th>
                    <th className="px-6 py-4 font-semibold">Created Info</th>
                    <th className="px-6 py-4 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentItems.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition">
                      <td className="px-6 py-4 font-bold text-slate-800">
                        {item.description}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-700">
                            {item.createdByFullName || "System"}
                          </span>
                          <span className="text-slate-400">
                            {formatDate(item.createdDate)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* ✅ Audit button desktop */}
                          <button
                            onClick={() => setAuditItem(item)}
                            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="View Audit Info"
                          >
                            <Clock size={18} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedData(item);
                              setIsAddModalVisible(true);
                            }}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleSoftDelete(item.id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentItems.length === 0 && (
                    <tr>
                      <td
                        colSpan="3"
                        className="px-6 py-10 text-center text-slate-400"
                      >
                        No terms found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-slate-100 md:block hidden">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredTerms.length}
                currentPage={currentPage}
                paginate={paginate}
              />
            </div>

            {/* 2. MOBILE VIEW */}
            <div className="md:hidden flex flex-col gap-3">
              {currentItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col gap-3"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                        <Layers size={16} />
                      </div>
                      <h3 className="font-bold text-slate-800 text-base">
                        {item.description}
                      </h3>
                    </div>
                    <span className="text-[10px] font-medium text-slate-400 bg-slate-50 px-2 py-1 rounded-full border border-slate-100">
                      {formatDate(item.createdDate)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-50 mt-1">
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                      <User size={12} className="text-slate-300" />
                      {item.createdByFullName || "System"}
                    </div>

                    <div className="flex items-center gap-3">
                      {/* ✅ Audit button mobile */}
                      <button
                        onClick={() => setAuditItem(item)}
                        className="text-xs font-semibold text-amber-500 hover:text-amber-700 flex items-center gap-1"
                      >
                        <Clock size={14} /> Audit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedData(item);
                          setIsAddModalVisible(true);
                        }}
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                      >
                        <Edit size={14} /> Edit
                      </button>
                      <button
                        onClick={() => handleSoftDelete(item.id)}
                        className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {currentItems.length === 0 && (
                <div className="text-center py-10 text-slate-400 bg-white rounded-2xl border border-slate-100">
                  No terms found.
                </div>
              )}

              <div className="mt-2 pb-6">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredTerms.length}
                  currentPage={currentPage}
                  paginate={paginate}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* --- Add/Edit Modal --- */}
      {isAddModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <AddTerms
              onClose={() => {
                setIsAddModalVisible(false);
                fetchData();
              }}
              refreshData={fetchData}
              selectedData={selectedData}
              // ✅ No currentUser prop — handled internally via Redux
            />
          </div>
        </div>
      )}

      {/* --- History Modal --- */}
      <DeletedHistoryModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />

      {/* ✅ AuditInfoModal */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.description}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default AllTerms;
